import React from "react";
import t from "../../../../../../i18n";
import { colors } from "@ui/Theme";
import { stableColor } from "@ui";
import { useQOI } from "../../../useQOI";
import {
  REST_PROJECT_AGGREGATION_TARGET_ID,
  useFailureAggegation,
  useFailureAggregationTargets,
} from "../useFailureAggregation";
import { aggregateSerieValues, aggregationByFailureAndPI } from "../../../utils/series";
import { REPORTING_POINT_METADATA } from "../../../../../Project/Chart/PointMetadata";
import { useTranslations } from "../../../../../../api/useTranslations";
import { getPointProjectCount } from "../../../utils/projectCount";
import type { DiagnosisReporting } from "../../../../../../api/useReporting";
import type { Reparability } from "../../../../../../api/Reparability";
import { reparabilities, REPARABILITY_COLOR } from "../../../../../../api/Reparability";

export const FLEET_FAILURE_IMPACT = "FleetFailureImpact";
export const FLEET_FAILURE_RESIDUAL_IMPACT = "FleetFailureResidualImpact";

const aggregatedSeries: Highcharts.SeriesColumnOptions = {
  type: "column",
  yAxis: "diagnosis-internal-losses",
  stack: 1,
  stacking: "normal",
  replicateInNavigator: false,
  borderWidth: 0,
};

function isReparability(value: string): value is Reparability {
  return reparabilities.includes(value as Reparability);
}

function createAggregatedSerie(
  target: { id: string; name: string },
  index: number,
  data: { x: number; y: number | null }[],
) {
  const color =
    target.id === REST_PROJECT_AGGREGATION_TARGET_ID
      ? "#888888"
      : isReparability(target.id)
        ? REPARABILITY_COLOR[target.id]
        : stableColor(target.id);

  return {
    ...aggregatedSeries,
    id: `InternalGapAggregation-${target.id}-${index}`,
    name: target.name,
    color,
    data: data.filter(({ y }) => y !== null),
    index,
    showInLegend: data.some(d => d.y !== 0),
  };
}

function flattenImpactValues(values: DiagnosisReporting["periods"][number]["values"]) {
  return values.flatMap(({ qoiId, projectId, expected, impacts }) =>
    impacts.map(({ failureId, reparability, value }) => ({
      qoiId,
      failureId,
      reparability,
      value,
      projectId,
      expected,
    })),
  );
}

export function useSeries(reporting: DiagnosisReporting) {
  const translations = useTranslations();
  const [selectedFailureAggregation] = useFailureAggegation({});
  const [qoi] = useQOI(reporting);
  const aggregationTargets = useFailureAggregationTargets(reporting);

  const totalInternalLossesSerie: Highcharts.SeriesColumnOptions = React.useMemo(
    () => ({
      id: FLEET_FAILURE_IMPACT,
      name: t("reporting.chart.aggregation.failure.total_losses", translations),
      type: "column",
      color: colors.raw.lightNitrogen100,
      yAxis: "diagnosis-internal-losses",
      data: [],
      replicateInNavigator: false,
      index: 0,
      showInLegend: true,
      legendIndex: 0,
      borderWidth: 0,
      stack: 2,
    }),
    [translations],
  );

  const totalResidualInternalLossesSeries: Highcharts.SeriesColumnOptions = React.useMemo(
    () => ({
      id: FLEET_FAILURE_RESIDUAL_IMPACT,
      name: t("reporting.chart.aggregation.failure.remaining_losses", translations),
      type: "column",
      color: colors.raw.carbon600,
      yAxis: "diagnosis-internal-losses",
      data: [],
      replicateInNavigator: false,
      index: 0,
      showInLegend: true,
      borderWidth: 0,
      stack: 1,
      stacking: "normal",
    }),
    [translations],
  );

  return React.useMemo(() => {
    const qois = Object.values(reporting.qoiById).filter(({ name }) => name === qoi?.name);
    const qoiIds = new Set(qois.map(({ id }) => id));
    const qoiProjectIds = new Set(qois.map(({ projectId }) => projectId));

    totalInternalLossesSerie.data = reporting.periods.map(
      ({ period, driftedProjectIds, values }) => ({
        x: period,
        y:
          aggregateSerieValues(
            values,
            (currentValue, value, expected) =>
              (currentValue ?? 0) + ((value ?? 0) - (expected ?? 0)),
            value => reporting.qoiById[value.qoiId]?.name === qoi?.name,
          ) ?? 0,
        custom: {
          type: REPORTING_POINT_METADATA,
          ...getPointProjectCount(qoiIds, qoiProjectIds, values, driftedProjectIds),
        },
      }),
    );

    const aggregatedSeries = aggregationTargets.map((target, i) =>
      createAggregatedSerie(
        target,
        i,
        reporting.periods.map(({ period, driftedProjectIds, values }) => ({
          x: period,
          y: aggregateSerieValues(
            flattenImpactValues(values),
            (currentValue, value) => (currentValue ?? 0) + (value ?? 0),
            aggregationByFailureAndPI(
              reporting,
              qoi?.name ?? null,
              selectedFailureAggregation,
              target.id,
            ),
          ),
          custom: {
            type: REPORTING_POINT_METADATA,
            ...getPointProjectCount(qoiIds, qoiProjectIds, values, driftedProjectIds),
          },
        })) || [],
      ),
    );

    totalResidualInternalLossesSeries.data = aggregatedSeries.reduce<
      Highcharts.PointOptionsObject[]
    >(
      (acc, serie /* , index */) =>
        acc.map(point => {
          const seriePoint = serie.data.find(({ x }) => x === point.x);
          return {
            x: point.x,
            y: (point.y ?? 0) - (seriePoint?.y ?? 0),
          };
        }),
      totalInternalLossesSerie.data as Highcharts.PointOptionsObject[],
    );

    return [
      totalInternalLossesSerie,
      ...aggregatedSeries,
      totalResidualInternalLossesSeries,
    ].filter(serie => (serie.data as Highcharts.PointOptionsObject[])?.some(({ y }) => y !== 0));
  }, [
    aggregationTargets,
    reporting,
    selectedFailureAggregation,
    qoi,
    totalInternalLossesSerie,
    totalResidualInternalLossesSeries,
  ]);
}
